import type { ReactNode } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

// Small field primitives shared by the profile and promotions editors (and so by both hosts:
// the staff page now, the client portal later). Presentational only, no I/O.

/** A disabled fieldset: the portal's read-only view of an editor. Every native control and button inside is disabled; nothing is hidden. */
export function ReadOnlyShell({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <fieldset disabled className={`m-0 min-w-0 border-0 p-0 ${className ?? ''}`} data-testid="read-only-editor">
      {children}
    </fieldset>
  );
}

export function Group({ title, help, children }: { title: string; help?: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border p-4">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {help ? <p className="mt-0.5 text-xs text-muted-foreground">{help}</p> : null}
      </div>
      {children}
    </section>
  );
}

export function TextField({
  id,
  label,
  value,
  onChange,
  multiline = false,
  placeholder,
  help,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  placeholder?: string;
  help?: string;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      {multiline ? (
        <Textarea id={id} rows={3} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      ) : (
        <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}
      {help ? <p className="mt-1 text-xs text-muted-foreground">{help}</p> : null}
    </div>
  );
}

export function CheckField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      {label}
    </label>
  );
}

export type Column<T> = {
  key: keyof T & string;
  label: string;
  kind?: 'text' | 'textarea' | 'check';
  wide?: boolean;
};

let rowCounter = 0;
export function newRowId(): string {
  rowCounter += 1;
  return `r${Date.now().toString(36)}${rowCounter}`;
}

/** An editable list of rows, one card per row. Generic over the row type. */
export function RowList<T extends { id: string }>({
  title,
  rows,
  columns,
  onChange,
  blank,
  addLabel,
}: {
  title: string;
  rows: T[];
  columns: Column<T>[];
  onChange: (next: T[]) => void;
  blank: () => T;
  addLabel: string;
}) {
  const update = (i: number, patch: Partial<T>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{title}</p>
      {rows.map((row, i) => (
        <div key={row.id} className="space-y-2 rounded-md border p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            {columns.map((c) => {
              const value = row[c.key] as unknown;
              const id = `${title}-${i}-${c.key}`.replace(/\s+/g, '-');
              if (c.kind === 'check') {
                return (
                  <div key={c.key} className="sm:col-span-2">
                    <CheckField label={c.label} checked={value === true} onChange={(v) => update(i, { [c.key]: v } as Partial<T>)} />
                  </div>
                );
              }
              return (
                <div key={c.key} className={c.wide || c.kind === 'textarea' ? 'sm:col-span-2' : ''}>
                  <TextField
                    id={id}
                    label={c.label}
                    value={String(value ?? '')}
                    multiline={c.kind === 'textarea'}
                    onChange={(v) => update(i, { [c.key]: v } as Partial<T>)}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => onChange(rows.filter((_r, j) => j !== i))}>
              <Trash2 className="h-4 w-4" />
              Remove
            </Button>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...rows, blank()])}>
        <Plus className="h-4 w-4" />
        {addLabel}
      </Button>
    </div>
  );
}
